<template>
    <div
        class="mb-1 transition duration-200 ease-in-out rounded"
        :class="{
            'border-2 border-[#4299E1]': isSelected === true,
            'border-2 border-gray-300': isSelected === false,
        }"
        @click="changeDefaultPaymentMethod"
    >
        <div
            class="flex cursor-pointer items-center justify-between px-4 pb-[14px] pt-[9px]"
        >
            <div class="flex items-center">
                <img
                    class="w-10 mr-5"
                    :src="paymentMethod.imageUrl?.includes('amex') ? 'https://assets.braintreegateway.com/payment_method_logo/american_express.png' : paymentMethod.imageUrl"
                />
                <div v-if="isCreditCard(paymentMethod)" class="flex flex-col justify-center h-10">
                    <h4
                        class="text-base font-bold leading-normal text-gray-800"
                    >
                        Ending in {{ paymentMethod.last4 }}
                    </h4>
                    <p
                        class="-mt-[3px] flex flex-row text-xs font-medium leading-normal text-gray-600"
                    >
                        <span class="hidden pr-1 sm:block"
                            >Expiration date:</span
                        >{{ paymentMethod.expirationDate }}
                    </p>
                </div>
                <div
                    v-if="isPayPal(paymentMethod)"
                    class="flex flex-col justify-center h-10"
                >
                    <h4
                        class="text-base font-bold leading-normal text-gray-800"
                    >
                        {{ paymentMethod.email }}
                    </h4>
                </div>
            </div>
            <div class="flex items-center">
                <div
                    v-if="!paymentMethod.default"
                    v-tooltip="{
                        content: isPaymentMethodInUse
                            ? 'You can\'t delete a payment method that is being used in a&nbsp;subscription. Please assign a new payment method to the&nbsp;subscription and try again.'
                            : '',
                        delay: {
                            show: 250,
                            hide: 150,
                        },
                        theme: 'info-tooltip',
                    }"
                    class="inline text-sm font-medium text-right"
                    :class="{
                        'cursor-not-allowed text-gray-500':
                            isPaymentMethodInUse,
                        'hover:text-topol-blue-600': !isPaymentMethodInUse,
                    }"
                    @click.stop="deletePaymentMethod"
                >
                    {{
                        isCreditCard(paymentMethod)
                            ? 'Remove card'
                            : 'Remove PayPal'
                    }}
                </div>

                <div v-if="isSelected" class="ml-6">
                    <svg
                        width="40"
                        height="40"
                        viewBox="0 0 40 40"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <circle cx="20" cy="20" r="20" fill="#38A169" />
                        <path
                            d="M11 20.5L17.5 27L29.5 15"
                            stroke="white"
                            stroke-width="3"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </div>
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
import {
    IPaymentMethod,
    ICreditCardPaymentMethod,
    IPayPalPaymentMethod,
} from '@/types/Subscription/IPaymentMethod';
import { PropType } from 'vue';

const props = defineProps({
    paymentMethod: {
        type: Object as PropType<IPaymentMethod>,
        required: true,
    },
    isSelected: {
        type: Boolean,
        required: true,
    },
});

const emit = defineEmits(['deletePaymentMethod', 'changeDefaultPaymentMethod']);

const changeDefaultPaymentMethod = () => {
    emit('changeDefaultPaymentMethod', props.paymentMethod);
};

const { teamFeatureDisabled } = useFeatures();

const isStripeEnabled = !teamFeatureDisabled('stripe');

const isPaymentMethodInUse = computed(() => {
    //TODO: Check if the payment method is in use
    if (isStripeEnabled) {
        return false;
    }

    return props.paymentMethod.subscriptions.some(
        (subscription) => subscription.status !== 'Canceled'
    );
});

const deletePaymentMethod = () => {
    if (isPaymentMethodInUse.value) {
        return;
    }

    emit('deletePaymentMethod', props.paymentMethod);
};

function isCreditCard(
    method: IPaymentMethod
): method is ICreditCardPaymentMethod {
    return (method as ICreditCardPaymentMethod).last4 !== undefined;
}

function isPayPal(method: IPaymentMethod): method is IPayPalPaymentMethod {
    return (method as IPayPalPaymentMethod).email !== undefined;
}
</script>
